import { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { BannerContext } from '../contexts/BannerContext';
import { useTradeJournals } from './useTradeJournals';
import useIsMobile from './useIsMobile';

export const normalizeJournalTemplate = (tmpl) => ({
  ...tmpl,
  fields: (tmpl.fields || []).map((f, i) => ({
    ...f,
    position: typeof f.position === 'number' ? f.position : i,
  })),
});

/**
 * Journal template state, autosave, and field-editing helpers (Settings + dashboard).
 * @param {object} [options]
 * @param {object|null} [options.seedTemplate] - start from this template (no server load)
 * @param {boolean} [options.autoLoadSaved=true] - load saved template from API on mount
 * @param {boolean} [options.autoSave=true] - debounce-save edits to API
 * @param {boolean} [options.saveOnSelect=true] - persist immediately when picking a default template
 * @param {Function} [options.onTemplateChange] - called with (template, name) after the template is loaded or edited
 */
export function useJournalTemplateBuilder({
  seedTemplate = null,
  autoLoadSaved = true,
  autoSave = true,
  saveOnSelect = true,
  onTemplateChange = null,
} = {}) {
  const { showMessage } = useContext(BannerContext);
  const { getTradeJournalTemplate, saveTradeJournalTemplate } = useTradeJournals();
  const isMobile = useIsMobile(768, 1024);

  const [journalTemplate, setJournalTemplate] = useState(() =>
    seedTemplate ? normalizeJournalTemplate(seedTemplate) : { fields: [] }
  );
  const [templateName, setTemplateName] = useState(() => seedTemplate?.name || 'Default');
  const [optionInputs, setOptionInputs] = useState({});
  const [editingOption, setEditingOption] = useState({ fieldIdx: null, optIdx: null, value: '' });
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverState, setDragOverState] = useState({ index: null, position: null });
  const [promptModal, setPromptModal] = useState({ show: false, field: null, fieldIndex: null });
  const [saving, setSaving] = useState(false);

  const hasLoadedTemplate = useRef(!!seedTemplate);
  const autosaveTimerRef = useRef(null);
  const lastSavedTemplateRef = useRef(null);
  const seedKeyRef = useRef(seedTemplate?.name || null);
  const onTemplateChangeRef = useRef(onTemplateChange);
  const journalTemplateRef = useRef(journalTemplate);
  const templateNameRef = useRef(templateName);
  const autoSaveRef = useRef(autoSave);
  onTemplateChangeRef.current = onTemplateChange;
  journalTemplateRef.current = journalTemplate;
  templateNameRef.current = templateName;
  autoSaveRef.current = autoSave;

  useEffect(() => {
    if (!seedTemplate) return;
    const key = seedTemplate.name || JSON.stringify(seedTemplate.fields?.length);
    if (seedKeyRef.current === key && hasLoadedTemplate.current) return;
    seedKeyRef.current = key;
    const normalized = normalizeJournalTemplate(seedTemplate);
    setJournalTemplate(normalized);
    setTemplateName(seedTemplate.name || 'Default');
    hasLoadedTemplate.current = true;
    lastSavedTemplateRef.current = null;
  }, [seedTemplate]);

  const handleSelectTemplate = useCallback(async (template) => {
    const normalized = normalizeJournalTemplate(template);
    setJournalTemplate(normalized);
    setTemplateName(template.name);
    hasLoadedTemplate.current = true;

    if (!saveOnSelect) {
      lastSavedTemplateRef.current = null;
      showMessage(`Template "${template.name}" loaded. Review and save when ready.`, 'success');
      return;
    }

    try {
      await saveTradeJournalTemplate(normalized, template.name);
      lastSavedTemplateRef.current = JSON.stringify({ name: template.name, template: normalized });
      showMessage(`Template "${template.name}" loaded. You can edit it below.`, 'success');
    } catch {
      showMessage('Error loading template', 'danger');
    }
  }, [saveOnSelect, saveTradeJournalTemplate, showMessage]);

  const saveNow = useCallback(async () => {
    setSaving(true);
    try {
      const toSave = normalizeJournalTemplate(journalTemplate);
      const name = templateName || 'Default';
      await saveTradeJournalTemplate(toSave, name);
      lastSavedTemplateRef.current = JSON.stringify({ name, template: toSave });
      hasLoadedTemplate.current = true;
      showMessage(`Template "${name}" saved.`, 'success');
      return { template: toSave, name };
    } catch (err) {
      showMessage('Error saving template', 'danger');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [journalTemplate, templateName, saveTradeJournalTemplate, showMessage]);

  useEffect(() => {
    if (!autoLoadSaved || seedTemplate) return;
    const loadTemplate = async () => {
      try {
        const res = await getTradeJournalTemplate();
        if (res?.template) {
          const fields = Array.isArray(res.template.fields) ? res.template.fields : [];
          const sorted = fields
            .map((f, i) => ({ ...f, __i: i }))
            .sort((a, b) => (a.position ?? a.__i) - (b.position ?? b.__i))
            .map(({ __i, ...rest }) => rest);
          const normalized = normalizeJournalTemplate({ ...res.template, fields: sorted });
          setJournalTemplate(normalized);
          if (res.name) setTemplateName(res.name);
          hasLoadedTemplate.current = true;
          try {
            lastSavedTemplateRef.current = JSON.stringify({
              name: res.name || 'Default',
              template: normalized,
            });
          } catch (_) {}
        }
      } catch (_) {
        // No saved template yet
      }
    };
    loadTemplate();
  }, [autoLoadSaved, getTradeJournalTemplate, seedTemplate]);

  useEffect(() => {
    if (!autoSave) return;
    if (!hasLoadedTemplate.current) return;
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);

    autosaveTimerRef.current = setTimeout(async () => {
      try {
        const toSave = normalizeJournalTemplate(journalTemplate);
        const payloadString = JSON.stringify({ name: templateName || 'Default', template: toSave });
        if (lastSavedTemplateRef.current === payloadString) return;
        await saveTradeJournalTemplate(toSave, templateName || 'Default');
        lastSavedTemplateRef.current = payloadString;
      } catch (_) {}
    }, 400);

    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    };
  }, [autoSave, journalTemplate, templateName, saveTradeJournalTemplate]);

  useEffect(() => {
    if (!hasLoadedTemplate.current) return;
    onTemplateChangeRef.current?.(journalTemplate, templateName);
  }, [journalTemplate, templateName]);

  // Persist the latest edit if the builder unmounts before the debounce fires.
  useEffect(() => {
    return () => {
      if (!autoSaveRef.current || !hasLoadedTemplate.current) return;
      const toSave = normalizeJournalTemplate(journalTemplateRef.current);
      const name = templateNameRef.current || 'Default';
      const payloadString = JSON.stringify({ name, template: toSave });
      if (lastSavedTemplateRef.current === payloadString) return;
      lastSavedTemplateRef.current = payloadString;
      saveTradeJournalTemplate(toSave, name).catch(() => {
        if (lastSavedTemplateRef.current === payloadString) {
          lastSavedTemplateRef.current = null;
        }
      });
    };
  }, [saveTradeJournalTemplate]);

  const addField = useCallback(() => {
    if (isMobile) {
      setPromptModal({ show: true, field: null, fieldIndex: null });
      return;
    }
    const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
    setJournalTemplate((prev) => {
      const nextNumber = (prev.fields || []).length + 1;
      return {
        ...prev,
        fields: [...(prev.fields || []), { key: `field_${nextNumber}`, label: '', type: 'text', options: [] }],
      };
    });
    requestAnimationFrame(() => {
      requestAnimationFrame(() => window.scrollTo(0, scrollPosition));
    });
  }, [isMobile]);

  const updateField = useCallback((idx, updates) => {
    setJournalTemplate((prev) => {
      const fields = [...(prev.fields || [])];
      fields[idx] = { ...fields[idx], ...updates };
      return { ...prev, fields };
    });
  }, []);

  const removeField = useCallback((idx) => {
    setJournalTemplate((prev) => {
      const fields = [...(prev.fields || [])];
      fields.splice(idx, 1);
      return { ...prev, fields };
    });
  }, []);

  const handleSavePrompt = useCallback((fieldData, fieldIndex) => {
    const processedFieldData = { ...fieldData };
    if ((fieldData.type === 'single' || fieldData.type === 'multi') && Array.isArray(fieldData.options)) {
      processedFieldData.options = fieldData.options.map((opt) => {
        if (typeof opt === 'object' && opt !== null) {
          return String(opt.label || opt.value || opt);
        }
        return String(opt);
      });
    }

    if (fieldIndex != null) {
      setJournalTemplate((prev) => {
        const fields = [...(prev.fields || [])];
        const existingField = fields[fieldIndex];
        fields[fieldIndex] = {
          ...fields[fieldIndex],
          ...processedFieldData,
          key: existingField?.key || `field_${fieldIndex + 1}`,
        };
        return { ...prev, fields };
      });
    } else {
      setJournalTemplate((prev) => {
        const nextNumber = (prev.fields || []).length + 1;
        return {
          ...prev,
          fields: [...(prev.fields || []), { key: `field_${nextNumber}`, ...processedFieldData }],
        };
      });
    }
  }, []);

  const handleEditPrompt = useCallback((idx) => {
    setJournalTemplate((prev) => {
      const field = prev.fields?.[idx] ?? null;
      setPromptModal({ show: true, field, fieldIndex: idx });
      return prev;
    });
  }, []);

  const addOptionToken = useCallback((idx, token) => {
    const value = (token || '').trim();
    if (!value) return;
    const existing = journalTemplate.fields?.[idx]?.options || [];
    const exists = existing.some((opt) => {
      const optValue = typeof opt === 'string' ? opt : (opt.value || opt.label || String(opt));
      return optValue === value;
    });
    if (exists) return;
    updateField(idx, { options: [...existing, value] });
    setOptionInputs((prev) => ({ ...prev, [idx]: '' }));
  }, [journalTemplate.fields, updateField]);

  const removeOptionToken = useCallback((idx, optIdx) => {
    const existing = journalTemplate.fields?.[idx]?.options || [];
    updateField(idx, { options: existing.filter((_, i) => i !== optIdx) });
  }, [journalTemplate.fields, updateField]);

  const beginEditOption = useCallback((idx, optIdx, current) => {
    const currentValue = typeof current === 'string' ? current : (current.value || current.label || String(current));
    setEditingOption({ fieldIdx: idx, optIdx, value: currentValue });
  }, []);

  const commitEditOption = useCallback(() => {
    const { fieldIdx, optIdx, value } = editingOption;
    if (fieldIdx == null || optIdx == null) return;
    const v = (value || '').trim();
    const existing = journalTemplate.fields?.[fieldIdx]?.options || [];
    if (!v) {
      updateField(fieldIdx, { options: existing.filter((_, i) => i !== optIdx) });
    } else {
      const next = existing.map((o, i) => {
        if (i === optIdx) return v;
        return typeof o === 'string' ? o : (o.label || o.value || String(o));
      });
      const unique = next.filter((o, i, arr) => {
        const oValue = typeof o === 'string' ? o : (o.value || o.label || String(o));
        return arr.findIndex((a) => {
          const aValue = typeof a === 'string' ? a : (a.value || a.label || String(a));
          return aValue === oValue;
        }) === i;
      });
      updateField(fieldIdx, { options: unique });
    }
    setEditingOption({ fieldIdx: null, optIdx: null, value: '' });
  }, [editingOption, journalTemplate.fields, updateField]);

  const cancelEditOption = useCallback(() => {
    setEditingOption({ fieldIdx: null, optIdx: null, value: '' });
  }, []);

  const moveField = useCallback((idx, direction) => {
    setJournalTemplate((prev) => {
      const fields = [...(prev.fields || [])];
      const newIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (newIdx < 0 || newIdx >= fields.length) return prev;
      const tmp = fields[idx];
      fields[idx] = fields[newIdx];
      fields[newIdx] = tmp;
      return { ...prev, fields };
    });
  }, []);

  const reorderFields = useCallback((fromIdx, toIdx) => {
    if (fromIdx == null || toIdx == null || fromIdx === toIdx) return;
    setJournalTemplate((prev) => {
      const fields = [...(prev.fields || [])];
      if (fromIdx < 0 || fromIdx >= fields.length || toIdx < 0 || toIdx >= fields.length) return prev;
      const [moved] = fields.splice(fromIdx, 1);
      fields.splice(toIdx, 0, moved);
      return { ...prev, fields };
    });
  }, []);

  return {
    isMobile,
    journalTemplate,
    templateName,
    setTemplateName,
    optionInputs,
    setOptionInputs,
    editingOption,
    setEditingOption,
    dragIndex,
    setDragIndex,
    dragOverState,
    setDragOverState,
    promptModal,
    setPromptModal,
    handleSelectTemplate,
    saveNow,
    saving,
    addField,
    updateField,
    removeField,
    handleSavePrompt,
    handleEditPrompt,
    addOptionToken,
    removeOptionToken,
    beginEditOption,
    commitEditOption,
    cancelEditOption,
    moveField,
    reorderFields,
  };
}
